import type { RoutingSkuTargetRecord, RoutingTargetDisplayNamesResponse } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { targetDisplayName, targetSupplyLabel } from './TenantRoutingTargetSupply.tsx';
import type { TargetShare } from './firstAttemptShare.ts';

/**
 * The selected SKU's first-attempt split, at the top of its target pool
 * (#1495).
 *
 * This is the answer to the question the page exists to ask — "this SKU fans
 * out to which upstreams, in what proportion" — which previously existed only
 * as a `weight` column an operator had to normalise in their head.
 *
 * On colour: the Deck palette (`src/ui/tokens.ts`) has no categorical hues, and
 * the three lamp colours are status semantics that would misread as health if
 * borrowed for identity. So every segment is the same `signal` orange, split by
 * hairline gaps, and identity comes from the legend text below — which also
 * means the bar extends to any number of targets instead of running out of
 * distinct colours at three. The bar itself is `aria-hidden` decoration
 * (DESIGN.md §9); the legend is the readable copy.
 */

interface Props {
  /** Targets in the pool's display order — segments follow the rows below. */
  readonly targets: readonly RoutingSkuTargetRecord[];
  readonly displayNames?: RoutingTargetDisplayNamesResponse;
  readonly shares: readonly TargetShare[];
  readonly skuId: string;
}

/** Bucket names distinguish multiple keys of one upstream without exposing IDs. */
export function TenantRoutingPoolShareBar({ targets, shares, skuId, displayNames }: Props): JSX.Element {
  const t = useT();
  const unnamed = t('tenant_routing.supply.unnamed');
  const targetsById = new Map(targets.map((target) => [target.id, target]));

  const active = shares.filter((share) => share.kind === 'active');
  const reserveCount = shares.filter((share) => share.kind === 'reserve').length;
  let labels = active.map((share) => {
    const target = targetsById.get(share.targetId);
    return target === undefined ? unnamed : targetSupplyLabel(target, displayNames, unnamed);
  });
  // Only visible collisions gain another readable identity field, never an ID.
  for (const field of ['upstream_channel_slug', 'upstream_model_name'] as const) {
    const counts = new Map<string, number>();
    for (const label of labels) counts.set(label, (counts.get(label) ?? 0) + 1);
    labels = labels.map((label, index) => (counts.get(label) ?? 0) > 1
      ? `${label} · ${targetDisplayName(targetsById.get(active[index]!.targetId)?.[field], unnamed)}`
      : label);
  }

  return (
    <div
      className="flex flex-col gap-1.5 border-b border-line-soft/50 px-4 pb-3 pt-1"
      data-testid={`tenant-routing-pool-share-${skuId}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-label text-paper-mid">{t('tenant_routing.pool.share_title')}</span>
        <span className="text-micro text-paper-low">{t('tenant_routing.pool.share_hint')}</span>
      </div>

      {active.length === 0 ? (
        <p className="m-0 text-micro text-paper-low" data-testid={`tenant-routing-pool-share-none-${skuId}`}>
          {t('tenant_routing.pool.share_none')}
        </p>
      ) : (
        <>
          {/*
            `gap-px` over the container's own hairline fill is what separates
            adjacent segments: same colour on both sides, so the boundary is a
            slit rather than a hue change. The gaps make the children total
            slightly over 100%, which `overflow-hidden` clips — at most one
            pixel per segment, well under the rounding the percentages already
            carry.
          */}
          <div className="flex h-2 gap-px overflow-hidden rounded-full bg-line-soft" aria-hidden="true">
            {active.map((share) => (
              <div
                key={share.targetId}
                className="h-full flex-none bg-signal first:rounded-l-full last:rounded-r-full"
                style={{ width: `${String(share.percent)}%` }}
              />
            ))}
          </div>

          <ul className="m-0 flex list-none flex-wrap gap-x-3 gap-y-0.5 p-0">
            {active.map((share, index) => {
              const target = targetsById.get(share.targetId);
              const label = labels[index]!;
              return (
                <li
                  key={share.targetId}
                  className="flex items-baseline gap-1 text-micro"
                  data-testid={`tenant-routing-pool-share-legend-${share.targetId}`}
                  title={t('tenant_routing.pool.share_label', {
                    channel: target?.upstream_channel_slug ?? label,
                    percent: share.percent,
                  })}
                >
                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-signal" />
                  <span
                    className="min-w-0 max-w-xs truncate text-paper-mid"
                    title={label}
                    data-testid={`tenant-routing-pool-share-name-${share.targetId}`}
                  >
                    {label}
                  </span>
                  <span className="font-mono text-paper">{share.percent}%</span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {reserveCount > 0 ? (
        <p className="m-0 text-micro text-paper-low" data-testid={`tenant-routing-pool-share-reserve-${skuId}`}>
          {t('tenant_routing.pool.share_reserve_count', { count: reserveCount })}
        </p>
      ) : null}
    </div>
  );
}
